import { CheckCircleIcon } from '@heroicons/react/outline'
import StrategyCallButton from './StrategyCallButton'

const COLORS = {
    dark: '#1F2937',
    accent: '#ED5C2F',
    borderSoft: 'color-mix(in oklab, #1f2937 10%, transparent)',
    text50: 'color-mix(in oklab, #1f2937 50%, transparent)',
    text55: 'color-mix(in oklab, #1f2937 55%, transparent)',
    text70: 'color-mix(in oklab, #1f2937 70%, transparent)',
    text80: 'color-mix(in oklab, #1f2937 80%, transparent)',
    panelBg: 'color-mix(in oklab, #fff7ed 70%, transparent)',
    sectionBg: 'color-mix(in oklab, #fff7ed 60%, transparent)',
    hoverBorder: 'color-mix(in oklab, #ed5c2f 40%, transparent)',
}

const defaultContent = {
    number: '02',
    eyebrow: 'Mailing Lists & Data',
    titlePrefix: 'Better lists. ',
    titleHighlight: 'Better response.',
    description:
        'Direct mail performance starts with who you mail. We help teams source the right audiences, clean and validate the data, segment by relevant criteria, and prepare lists for production, so budget goes toward households that actually fit the offer.',
    includedHeading: "What's included",
    includedItems: [
        'Custom list sourcing',
        'Demographic targeting',
        'Geographic & radius targeting',
        'Property & homeowner data',
        'NCOA & address validation',
        'Storm data',
        'Segmentation & list splits',
        'Consumer and company data',
    ],
    cta: {
        label: 'Request a list quote',
        href: '/contact-us',
    },
    samples: [
        {
            label: 'Real Estate Investors',
            title: 'Absentee owners, 5+ year tenure',
            detail: 'High equity · Out-of-state mailing address',
            count: '18,420',
            active: true,
        },
        {
            label: 'Real Estate Agents',
            title: 'Likely sellers in farm area',
            detail: '8+ year tenure · Life-event triggers',
            count: '12,860',
        },
        {
            label: 'Roofers',
            title: 'Storm-affected homeowners',
            detail: 'Recent hail / wind · Roof age 15+ yrs',
            count: '9,140',
        },
        {
            label: 'HVAC',
            title: 'Aging systems, owner-occupied',
            detail: 'Home age 18+ yrs · HHI $80k+',
            count: '7,780',
        },
    ],
}

function IncludedItem({ children }) {
    return (
        <div className="flex items-start gap-2">
            <CheckCircleIcon
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: COLORS.accent }}
            />
            <p className="text-sm" style={{ color: COLORS.text80 }}>
                {children}
            </p>
        </div>
    )
}

function SampleCard({ label, title, detail, count, active = false }) {
    const cardStyle = active
        ? {
              backgroundColor: '#ffffff',
              borderColor: COLORS.accent,
              borderWidth: '2px',
              boxShadow:
                  '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          }
        : {
              background: COLORS.panelBg,
              borderColor: COLORS.borderSoft,
          }

    return (
        <div
            className="flex items-center justify-between rounded-xl border px-5 py-4 transition-all duration-200 hover:-translate-y-1 hover:border-[#ed5c2f]/40 hover:bg-white hover:shadow-lg"
            style={cardStyle}>
            <div className="flex flex-col">
                <div
                    className="text-xs leading-7 font-bold uppercase tracking-[1.2px]"
                    style={{
                        color: active ? COLORS.accent : COLORS.text50,
                    }}>
                    {label}
                </div>
                <div
                    className="text-sm leading-7 font-bold"
                    style={{ color: COLORS.dark }}>
                    {title}
                </div>
                <div
                    className="mt-0.5 text-xs leading-7"
                    style={{ color: COLORS.text55 }}>
                    {detail}
                </div>
            </div>

            <div className="text-right">
                <div
                    className="text-2xl font-semibold tabular-nums"
                    style={{ color: active ? COLORS.accent : COLORS.dark }}>
                    {count}
                </div>
                <div
                    className="text-xs font-semibold uppercase tracking-[1.2px]"
                    style={{ color: COLORS.text50 }}>
                    records
                </div>
            </div>
        </div>
    )
}

export default function MailingListsDataSection(props) {
    const number = props?.number || defaultContent.number
    const eyebrow = props?.eyebrow || defaultContent.eyebrow
    const titlePrefix = props?.titlePrefix || defaultContent.titlePrefix
    const titleHighlight =
        props?.titleHighlight || defaultContent.titleHighlight
    const description = props?.description || defaultContent.description
    const includedHeading =
        props?.includedHeading || defaultContent.includedHeading
    const includedItems =
        Array.isArray(props?.includedItems) && props.includedItems.length > 0
            ? props.includedItems
            : defaultContent.includedItems
    const cta = props?.cta || defaultContent.cta
    const samples =
        Array.isArray(props?.samples) && props.samples.length > 0
            ? props.samples
            : defaultContent.samples

    return (
        <section
            id="mailing-lists-data"
            className="border-y scroll-mt-24"
            style={{
                background: COLORS.sectionBg,
                borderColor: COLORS.borderSoft,
            }}>
            <div className="container mx-auto px-4 py-16 lg:py-24">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <div
                        className="flex flex-col gap-3 rounded-3xl border bg-white p-6 shadow-lg transition-shadow duration-300 hover:shadow-xl lg:order-2"
                        style={{ borderColor: COLORS.borderSoft }}>
                        {samples.map((sample) => (
                            <SampleCard
                                key={`${sample.label}-${sample.count}`}
                                {...sample}
                            />
                        ))}
                    </div>

                    <div className="flex flex-col gap-5 lg:order-1">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ed5c2f]">
                                <div className="text-sm font-bold text-white">
                                    {number}
                                </div>
                            </div>
                            <div
                                className="text-xs leading-7 tracking-[1.2px] uppercase"
                                style={{ color: COLORS.text50 }}>
                                {eyebrow}
                            </div>
                        </div>

                        <h2
                            className="text-3xl font-[700] tracking-[-1.1px] md:text-[44px] md:leading-[1.1]"
                            style={{ color: COLORS.dark }}>
                            {titlePrefix}
                            <span style={{ color: COLORS.accent }}>
                                {titleHighlight}
                            </span>
                        </h2>

                        <p
                            className="text-lg leading-8"
                            style={{ color: COLORS.text70 }}>
                            {description}
                        </p>

                        <div
                            className="mt-2 rounded-2xl border bg-white p-6"
                            style={{
                                borderColor:
                                    'color-mix(in oklab, #ed5c2f 15%, transparent)',
                            }}>
                            <div
                                className="mb-3 text-xs font-bold uppercase tracking-[1.2px]"
                                style={{ color: COLORS.accent }}>
                                {includedHeading}
                            </div>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-2">
                                {includedItems.map((item, index) => (
                                    <IncludedItem key={`${item}-${index}`}>
                                        {item}
                                    </IncludedItem>
                                ))}
                            </div>
                        </div>

                        {cta?.label ? (
                            <div>
                                <StrategyCallButton className="inline-flex w-fit rounded-full bg-rtl-gradient px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:opacity-95">
                                    {cta.label}
                                </StrategyCallButton>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    )
}
